import React from 'react';
import { createPortal } from 'react-dom';
import { X, LogIn, UserPlus, ShoppingCart, MessageCircle, Heart, Star } from 'lucide-react';
import type { AuthFeature } from '../../hooks/useAuthPrompt';
import { useScrollLock } from '../../hooks/useScrollLock';

interface AuthPromptModalProps {
  isOpen: boolean;
  onClose: () => void;
  feature: AuthFeature | null;
  onSignIn: () => void;
  onSignUp: () => void;
}

const featureConfig = {
  add_to_cart: {
    title: 'Sign in to Add to Cart',
    description: 'Please sign in or create an account to add items to your cart and continue shopping.',
    icon: ShoppingCart,
    color: 'bg-gray-900',
  },
  buy_now: {
    title: 'Sign in to Buy Now',
    description: 'Please sign in or create an account to proceed with your purchase.',
    icon: ShoppingCart,
    color: 'bg-red-600',
  },
  message_seller: {
    title: 'Sign in to Message Seller',
    description: 'Please sign in or create an account to contact the seller and ask questions about this product.',
    icon: MessageCircle,
    color: 'bg-blue-600',
  },
  wishlist: {
    title: 'Sign in to Save to Wishlist',
    description: 'Please sign in or create an account to save products to your wishlist for later.',
    icon: Heart,
    color: 'bg-red-600',
  },
  review: {
    title: 'Sign in to Write a Review',
    description: 'Please sign in or create an account to share your experience and help other customers.',
    icon: Star,
    color: 'bg-yellow-500',
  },
  checkout: {
    title: 'Sign in to Checkout',
    description: 'Please sign in or create an account to complete your purchase.',
    icon: ShoppingCart,
    color: 'bg-red-600',
  },
  general: {
    title: 'Sign in Required',
    description: 'Please sign in or create an account to access this feature.',
    icon: LogIn,
    color: 'bg-gray-900',
  },
};

export const AuthPromptModal: React.FC<AuthPromptModalProps> = ({
  isOpen,
  onClose,
  feature,
  onSignIn,
  onSignUp,
}) => {
  // Lock scroll when modal is open
  useScrollLock(isOpen);

  if (!isOpen || !feature) {
    return null;
  }

  // SECURITY: Runtime validation - ensure feature is valid key in featureConfig
  // This provides defense-in-depth in case props are manipulated at runtime
  // Fallback to 'general' if invalid feature is provided to prevent component crash
  const config = featureConfig[feature as keyof typeof featureConfig] || featureConfig.general;
  const Icon = config.icon;

  const modal = (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 transition-opacity"
        style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, willChange: 'opacity' }}
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 transform transition-all z-10">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors z-20"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Content */}
        <div className="p-6 sm:p-8">
          {/* Icon */}
          <div className={`w-16 h-16 ${config.color} rounded-full flex items-center justify-center mx-auto mb-4`}>
            <Icon className="w-8 h-8 text-white" />
          </div>

          {/* Title */}
          <h2 className="text-2xl font-bold text-gray-900 text-center mb-2">
            {config.title}
          </h2>

          {/* Description */}
          <p className="text-gray-600 text-center mb-6">
            {config.description}
          </p>

          {/* Action Buttons */}
          <div className="space-y-3">
            <button
              onClick={onSignIn}
              className="w-full bg-[#da2516] hover:bg-[#da2516]/90 text-white font-semibold py-3 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              <LogIn className="w-5 h-5" />
              Sign In
            </button>

            <button
              onClick={onSignUp}
              className="w-full bg-white border-2 border-[#da2516] text-[#da2516] hover:bg-red-50 font-semibold py-3 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              <UserPlus className="w-5 h-5" />
              Create Account
            </button>
          </div>

          {/* Footer text */}
          <p className="text-xs text-gray-500 text-center mt-4">
            New to Baakas? Creating an account is free and takes less than a minute.
          </p>
        </div>
      </div>
    </div>
  );

  // Render modal in a portal at document.body level to ensure it covers the entire viewport
  if (typeof document !== 'undefined' && document.body) {
    return createPortal(modal, document.body);
  }

  return modal;
};

export default AuthPromptModal;

